import { FC } from 'react';
import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import {
  COLOR_BY_ASSIGNMENT_STATUS,
  getSurveyPermissionName,
  surveyAssignmentStatuses,
  useSurvey,
  useSurveyGroupAssignments,
  useUpdateSurveyGroupAssignments,
} from 'store/surveys';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import qs from 'qs';
import { useMe } from 'state/Administrators';

interface Props {
  onSaveSuccess: () => void;
}

export const SurveyParticipants: FC<Props> = ({ onSaveSuccess }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { hasEditPermission } = useMe();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);

  const { data: survey } = useSurvey(surveyId);
  const { data: assignments, isLoading } = useSurveyGroupAssignments(surveyId);
  const hasSurveyEditPermission = survey
    ? hasEditPermission([getSurveyPermissionName(survey.type)])
    : false;

  const { mutate: updateAssignments, isLoading: isAssigning } =
    useUpdateSurveyGroupAssignments();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={assignments?.total} />
        </Stack>
      </TableToolbar>

      <Table
        data={assignments?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'groupName',
            header: t('surveys.groupsListGroupName'),
            cell: ({ row: { original: assignment } }) => (
              <Link
                label={assignment.groupName}
                to={{
                  pathname: ROUTES.employeeGroups.details.replace(
                    ':id',
                    assignment.groupId,
                  ),
                  search: qs.stringify({ tab: 'innerGroupAssignEmployees' }),
                }}
              />
            ),
          },
          {
            id: 'employeeCount',
            header: t('surveys.groupsListEmployeeCount'),
            cell: ({ row: { original: assignment } }) =>
              assignment.employeeCount,
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: assignment } }) => {
              return (
                <Typography
                  color={
                    COLOR_BY_ASSIGNMENT_STATUS[assignment.assignmentStatus]
                  }
                >
                  {t(`status.${assignment.assignmentStatus}`)}
                </Typography>
              );
            },
          },
        ]}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: surveyAssignmentStatuses.map((status) => ({
            label: t(`status.${status}`),
            onClick: async () => {
              updateAssignments(
                {
                  surveyId,
                  data: {
                    ...(status === 'on'
                      ? { assign: Object.keys(selectedRowIds) }
                      : { unassign: Object.keys(selectedRowIds) }),
                  },
                },
                {
                  onSuccess: () => {
                    predefinedToasts.success.updated();
                    setSelectedRowIds({});
                    onSaveSuccess();
                  },
                },
              );
            },
            disabled: !hasSurveyEditPermission,
          })),
          isLoading: isAssigning,
        }}
      />
    </TableWrapper>
  );
};
